<template>
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
      variant="discount"
    />
  </div>
  <div
    v-else
    ref="contentRef"
    class="page"
  >
    <riseup-header :flow-config-id="otsJobConfig.flowConfigId" />
    <div ref="content">
      <missing-data
        v-if="shouldShowMissingData"
        :background-color="'#205441'"
        :title-color="'white'"
        :subtitle-color="'white'"
        :button-color="'white'"
        :button-text-color="'#205441'"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <available-money
        :total-investable="totalInvestable"
        :is-data-missing="isAvailableMoneyDataMissing"
        @navigate-to-etoro="navigateToEtoro"
      />
      <numbers-breakdown
        :available-funds="availableFunds"
        :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
        :total-investable="totalInvestable"
        :valid-cashflow-months="validCashflowMonths"
        :is-data-missing="isNumbersBreakdownDataMissing"
        @navigate-to-etoro="navigateToEtoro"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <tips
        :tips="tips"
        :total-savings="totalSavings"
        :is-data-missing="isTipsDataMissing"
      />

      <in-out
        :is-data-missing="isInOutDataMissing"
        :last-four-months="lastFourMonths"
      />
      <invest-funds
        :is-data-missing="isInvestFundsDataMissing"
        :available-funds="availableFunds"
        @navigate-to-etoro="navigateToEtoro"
      />
      <spending-categories
        :monthly-category-averages="monthlyCategoryAverages"
        :is-data-missing="isSpendingCategoriesDataMissing"
        @navigate-to-etoro="navigateToEtoro"
      />
    </div>
  </div>
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getTrueLayerUrl } from '../../../api/trueLayer';
import useContentLoader from '../../../composables/useContentLoader';
import config from '../../../config';
import Segment from '../../../Segment';
import { useCashflow } from '../../../services/cashflowService';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { useSessionStore } from '../../../stores/session';
import { formatLastFourMonths } from '../../../utils/miscUtils';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import MissingData from '../shared-components/MissingData.vue';
import AvailableMoney from './components/AvailableMoney.vue';
import InOut from './components/InOut.vue';
import InvestFunds from './components/InvestFunds.vue';
import NumbersBreakdown from './components/NumbersBreakdown.vue';
import SpendingCategories from './components/SpendingCategories.vue';
import Tips from './components/Tips.vue';

const { insights, populateInsights } = useInsights();
const contentRef = ref(null);
const route = useRoute();
const { getOtsJobConfig } = useOtsJob();
const {
  monthlyCategoryAverages,
  monthlyInOut,
  availableFunds,
  monthlyAvgDisposableIncome,
  totalInvestable,
  loadCashflowData,
  isCashflowLoaded,
  validCashflowMonths,
} = useCashflow();

const ERROR_MESSAGES = {
  INSIGHTS_ONLY: 'Failed to load your saving tips. We are looking into it. Try again later.',
  CASHFLOW_ONLY: 'Oops! There was an error with your account data. We are looking into it. Try again later.',
};

const tips = ref([]);
const totalSavings = ref(0);
const otsJobConfig = await getOtsJobConfig();
const sessionStore = useSessionStore();
const errorMessage = ref('');
const errorReason = ref('');
const showErrorToast = ref(false);

const {
  showContentLoader,
  toggleLoader,
} = useContentLoader();

const isNumbersBreakdownDataMissing = computed(() =>
  availableFunds.value.total === 0
  && monthlyAvgDisposableIncome.value.total === 0
  && totalInvestable.value === 0);

const isSpendingCategoriesDataMissing = computed(() =>
  monthlyCategoryAverages.value.length < 3);

const isTipsDataMissing = computed(() => tips.value.length === 0);
const isAvailableMoneyDataMissing = computed(() => totalInvestable.value <= 0);
const isInvestFundsDataMissing = computed(() => validCashflowMonths.value < 3);

const isInOutDataMissing = computed(() => {
  if (!monthlyInOut.value || monthlyInOut.value.length < 2) {
    return true;
  }

  const itemsWithNonZeroValues = monthlyInOut.value.filter(month =>
    month.in !== 0 || month.out !== 0).length;
  const haveLessThan2months = itemsWithNonZeroValues < 2;
  const notAllMonthsDefined = !monthlyInOut.value.every(month => month.month);

  return haveLessThan2months || notAllMonthsDefined;
});

const shouldShowMissingData = computed(() =>
  isNumbersBreakdownDataMissing.value
  || isSpendingCategoriesDataMissing.value
  || isTipsDataMissing.value
  || isInOutDataMissing.value
  || isInvestFundsDataMissing.value);

const lastFourMonths = computed(() => formatLastFourMonths(monthlyInOut.value));

const handleErrors = (insightsFailed, cashflowFailed) => {
  if (!insightsFailed && !cashflowFailed) {
    return;
  }

  if (cashflowFailed) {
    errorMessage.value = ERROR_MESSAGES.CASHFLOW_ONLY;
    errorReason.value = 'cashflow_data_load_failed';
  } else {
    errorMessage.value = ERROR_MESSAGES.INSIGHTS_ONLY;
    errorReason.value = 'insights_data_load_failed';
  }

  showErrorToast.value = true;
};

onMounted(async () => {
  toggleLoader(true);
  const queryParam = route.query || null;
  const shouldReload = route.state?.shouldReload;

  let insightsFailed = false;
  let cashflowFailed = false;

  if (!insights.value || insights.value.length === 0 || shouldReload) {
    try {
      await populateInsights(otsJobConfig.jobId);
    } catch (error) {
      insightsFailed = true;
    }
  }

  Segment.trackUserGot('EtoroOtsCustomSuccessPageEntered', { fromEmail: queryParam ? queryParam.fromEmail : false, otsJobConfig });

  if ((insights.value && insights.value.length > 0) || shouldReload) {
    tips.value = insights.value.map(insight => ({
      text: insight.item.text,
      amount: insight.item.amount,
      index: insight.index,
    }));

    totalSavings.value = insights.value.reduce((sum, insight) => sum + insight.item.amount, 0);
  }

  if (!isCashflowLoaded.value || shouldReload) {
    try {
      await loadCashflowData(6);
    } catch (error) {
      cashflowFailed = true;
    }
  }

  handleErrors(insightsFailed, cashflowFailed);
  toggleLoader(false);
  Segment.trackUserGot('EtoroOtsCustomSuccessResults', {
    validCashflowMonths: validCashflowMonths.value,
    isAvailableMoneyDataMissing: isAvailableMoneyDataMissing.value,
    isNumbersBreakdownDataMissing: isNumbersBreakdownDataMissing.value,
    isTipsDataMissing: isTipsDataMissing.value,
    isInOutDataMissing: isInOutDataMissing.value,
    isSpendingCategoriesDataMissing: isSpendingCategoriesDataMissing.value,
    isInvestFundsDataMissing: isInvestFundsDataMissing.value,
    shouldShowMissingData: shouldShowMissingData.value,
    insightLoadFailed: insightsFailed,
    cashflowLoadFailed: cashflowFailed,
  });
});

const navigateToEtoro = () => {
  const { customerId } = sessionStore;
  Segment.trackUserGot('EtoroOtsCustomSuccessNavigateToEtoroClicked', { gclid: Cookies.get('gclid') });
  window.open(`${config.get().ruthUrl}/ots?customer_id=${customerId}&to=etoro`, '_blank');
};

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('EtoroConnectOtsAccountsConnectClicked', { gclid: Cookies.get('gclid') });
  window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId, fromSuccess: true }, config.get().otsEtoroSuccessEnv);
};
</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.content {
  margin-top: 30px;
  padding-left: 25px;
  padding-right: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.page {
  background-color: #13C636;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  font-family: CircularStdBook;
}

.card {
  flex: 0 0 250px;
  min-width: 250px;
  max-width: 250px;
  width: 250px;
  background-color: white;
  border-radius: 25px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  height: 330px;
  display: flex;
  flex-direction: column;
}

.divider-line {
  width: 90%;
  margin: 0 auto;
  margin-top: 30px;
  height: 1px;
  background-color: white;
}
</style>
